import { useTranslation } from 'react-i18next';
import { isApiError } from '@shared/api';
import { ConfirmDialog } from '@shared/ui';
import type { useAiContentDeletion } from '../model/use-ai-content-deletion';
import { errorKey } from '../model/editor';

/** Delete progress and recovery belong to the confirmation flow, never the profile/list. */
export function AiContentDeletionFeedback({
  deletion,
  canDelete,
  onClose,
  title,
}: {
  deletion: ReturnType<typeof useAiContentDeletion>;
  canDelete: boolean;
  onClose?: () => void;
  title?: string;
}) {
  const { t } = useTranslation();
  const {
    deleted,
    deletionState,
    monitor,
    error: commandError,
    unresolvedDelete,
    pending,
    refresh,
    remove,
    beginDelete,
    blocker,
    confirm,
    closeConfirmation,
    operationId,
  } = deletion;
  if (deleted) return null;
  const error = commandError ?? deletion.detail.error;
  const stale = isApiError(error) && [412, 428].includes(error.status);
  const immutable = isApiError(error) && error.code === 'AI_CONTENT_IMMUTABLE_REVISION';
  const denied = !canDelete || (isApiError(error) && [401, 403].includes(error.status));
  const checking = Boolean(
    operationId && deletionState === 'pending' && !monitor.error && !error && !deletion.denied
  );
  const busy = pending || checking;
  const description = unresolvedDelete
    ? t('aiContent.deletion.sameRetry') + (denied ? ` ${t('aiContent.errors.denied')}` : '')
    : stale
      ? t('aiContent.deletion.staleDescription')
      : immutable
        ? t('aiContent.deletion.immutableDescription')
        : error
          ? t(
              isApiError(error) && error.status === 401
                ? 'aiContent.read.signedOut'
                : errorKey(error)
            )
          : !canDelete
            ? t('aiContent.errors.denied')
            : monitor.error
              ? t('aiContent.deletion.monitorDescription')
              : deletionState === 'failed'
                ? t('aiContent.deletion.failedDescription')
                : t('aiContent.deletion.confirmDescription', {
                    title: title ?? deletion.detail.data?.data.title ?? t('crm.missingValue'),
                  });
  return (
    <>
      <ConfirmDialog
        open={blocker.status === 'blocked'}
        title={t('aiContent.deletion.unknown')}
        description={t('aiContent.deletion.sameRetry')}
        confirmLabel={t('aiContent.deletion.retrySame')}
        cancelLabel={t('common.cancel')}
        confirmButtonProps={{ disabled: true }}
        onConfirm={() => undefined}
        onOpenChange={(open) => {
          if (!open && blocker.status === 'blocked') blocker.reset();
        }}
      />
      <ConfirmDialog
        open={confirm}
        onOpenChange={(open) => {
          if (busy || unresolvedDelete) return;
          closeConfirmation(open);
          if (!open) onClose?.();
        }}
        title={t('aiContent.deletion.confirmTitle')}
        description={description}
        confirmLabel={t(
          unresolvedDelete
            ? denied
              ? 'aiContent.refresh'
              : 'aiContent.deletion.retrySame'
            : operationId
              ? checking
                ? 'aiContent.deletion.confirm'
                : 'aiContent.refresh'
              : error
                ? 'aiContent.deletion.reconfirm'
                : 'aiContent.deletion.confirm'
        )}
        cancelLabel={t('common.cancel')}
        confirmPending={busy}
        confirmButtonProps={{
          disabled: (!canDelete && !unresolvedDelete) || immutable || (denied && !unresolvedDelete),
        }}
        cancelButtonProps={{ disabled: unresolvedDelete }}
        closeOnConfirm={false}
        onConfirm={() => {
          if (unresolvedDelete && denied) refresh();
          else if (unresolvedDelete) void remove();
          else if (operationId) refresh();
          else if (error) void beginDelete();
          else void remove();
        }}
      />
    </>
  );
}
